import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import { motion } from 'framer-motion';
import { Search, Bookmark, ChevronRight, Sun, Thermometer, CalendarDays, Bell, X, Sprout, GraduationCap, Lock } from 'lucide-react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { cropImage } from '@/lib/cropImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Exact mobile theme tokens (src/theme/index.ts) — one product, both screens ──
const C = {
  sage: '#E5EBDD', cream: '#FAF6EC', forest: '#1A4331', forestDeep: '#0F2D1F',
  leaf: '#6B9F4A', leafDeep: '#4E7A33', gold: '#C9A85B', terracotta: '#C66B4A', lime: '#B4D330',
  text: '#2A3528', muted: '#5C665C', white: '#FFFFFF',
  leafTint: 'rgba(107,159,74,0.14)', goldTint: 'rgba(201,168,91,0.14)',
};
const CARD_SH = '0 6px 16px -2px rgba(26,67,49,0.12)';
// Txt variants (src/components/ui.tsx)
const V = {
  h1: { fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: 32, lineHeight: '38px', color: C.forest },
  h3: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 20, lineHeight: '26px', color: C.forest },
  title: { fontFamily: 'Outfit, sans-serif', fontWeight: 600, fontSize: 17, lineHeight: '23px', color: C.forest },
  bodySemi: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 15, lineHeight: '22px', color: C.text },
  small: { fontFamily: 'Manrope, sans-serif', fontWeight: 400, fontSize: 13, lineHeight: '19px', color: C.muted },
  label: { fontFamily: 'Manrope, sans-serif', fontWeight: 600, fontSize: 13, lineHeight: '18px', color: C.muted },
};
const DIFF = {
  beginner: { bg: 'rgba(107,159,74,0.14)', fg: '#4E7A33' },
  intermediate: { bg: 'rgba(201,168,91,0.14)', fg: '#8A6D2B' },
  advanced: { bg: 'rgba(198,107,74,0.12)', fg: '#C66B4A' },
};

function HeaderActions({ user }) {
  const [unread, setUnread] = useState(0);
  const [avatarError, setAvatarError] = useState(false);
  useEffect(() => {
    axios.get(`${API}/notifications`, { withCredentials: true })
      .then((r) => setUnread((Array.isArray(r.data) ? r.data : []).filter((x) => !x.read).length))
      .catch(() => {});
  }, []);
  return (
    <div className="flex items-center gap-4 flex-shrink-0 pt-1">
      <Link to="/notifications" data-testid="header-notifications" className="relative hg-press">
        <Bell className="w-5 h-5" style={{ color: C.forest }} />
        {unread > 0 && (
          <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#C66B4A] text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>
        )}
      </Link>
      <Link to="/settings" data-testid="header-profile" className="hg-press w-8 h-8 rounded-full overflow-hidden" style={{ background: '#8EAA97' }}>
        {user?.picture && !avatarError ? (
          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" onError={() => setAvatarError(true)} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white text-sm font-semibold">{(user?.name?.[0] || '?').toUpperCase()}</div>
        )}
      </Link>
    </div>
  );
}

function CropLibrary({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [crops, setCrops] = useState([]);
  const [calendar, setCalendar] = useState({});
  const [zoneLabel, setZoneLabel] = useState('');
  const [currentMonth, setCurrentMonth] = useState('');
  const [soil, setSoil] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sowNowOnly, setSowNowOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shortlist, setShortlist] = useState([]);
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [member, setMember] = useState(true);
  const [freeSet, setFreeSet] = useState(null);

  useEffect(() => {
    axios.get(`${API}/crops`).then((r) => setCrops(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/entitlements`, { withCredentials: true }).then((r) => { setMember(!!r.data?.member); setFreeSet(new Set(r.data?.handbook?.free_crop_ids || [])); }).catch(() => {});
    axios.get(`${API}/crops/regional-calendar`, { withCredentials: true }).then((r) => {
      const map = {};
      (r.data.crops || []).forEach((c) => { map[c.crop_id] = c; });
      setCalendar(map);
      setZoneLabel(r.data.zone_label || '');
      setCurrentMonth(r.data.current_month || '');
    }).catch(() => {});
    axios.get(`${API}/ai/coach-vitals`, { withCredentials: true }).then((r) => setSoil(r.data?.soil_temp || null)).catch(() => {});
    axios.get(`${API}/crops/shortlist`, { withCredentials: true }).then((r) => setShortlist(r.data.items || [])).catch(() => {});
  }, []);

  const savedSet = new Set(shortlist.map((s) => s.crop_id));
  const toggleSave = (e, crop) => {
    e.preventDefault(); e.stopPropagation();
    if (savedSet.has(crop.crop_id)) {
      setShortlist((prev) => prev.filter((s) => s.crop_id !== crop.crop_id));
      axios.delete(`${API}/crops/shortlist/${crop.crop_id}`, { withCredentials: true }).catch(() => {});
    } else {
      const item = { crop_id: crop.crop_id, crop_name: crop.name, category: crop.category };
      setShortlist((prev) => [item, ...prev]);
      axios.post(`${API}/crops/shortlist`, item, { withCredentials: true }).catch(() => {});
    }
  };

  const categories = useMemo(() => ['all', ...Array.from(new Set(crops.map((c) => c.category)))], [crops]);
  const filtered = useMemo(
    () => crops.filter((c) =>
      (category === 'all' || c.category === category)
      && (!search || c.name.toLowerCase().includes(search.toLowerCase()))
      && (!sowNowOnly || calendar[c.crop_id]?.currently_sowable)),
    [crops, category, search, sowNowOnly, calendar],
  );
  const sowableNow = useMemo(() => Object.values(calendar).filter((c) => c.currently_sowable).length, [calendar]);

  // Shared-element zoom: capture the card image rect, morph a fixed clone into a
  // hero panel, then route into the guide — matching the native tap-through.
  const openGuide = (e, crop) => {
    e.preventDefault();
    const imgEl = e.currentTarget.querySelector('img');
    const rect = (imgEl || e.currentTarget).getBoundingClientRect();
    const payload = {
      id: crop.crop_id,
      img: cropImage(crop.name, crop.category, crop.image_url),
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    };
    try { sessionStorage.setItem('hg_zoom_origin', JSON.stringify(payload)); } catch (err) { /* noop */ }
    setZoom(payload);
  };
  const ActivePill = ({ label, onClear, testid }) => (
    <button onClick={onClear} data-testid={testid} className="hg-press inline-flex items-center rounded-full" style={{ gap: 6, padding: '5px 10px', background: C.forest }}>
      <span style={{ ...V.label, fontSize: 12, color: C.cream }}>{label}</span>
      <X className="w-3 h-3" style={{ color: 'rgba(250,246,236,0.85)' }} />
    </button>
  );

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <h1 style={V.h1} className="flex-1">{t('crops.handbook')}</h1>
        <HeaderActions user={user} />
      </div>
      <p style={V.small} className="mt-1">{t('crops.handbookSubtitle')}</p>
      {!member && freeSet && (
        <button onClick={() => navigate('/paywall')} data-testid="handbook-taster-note" className="hg-press mt-2 inline-flex items-center rounded-full" style={{ gap: 6, background: C.forestDeep, padding: '6px 12px' }}>
          <Lock className="w-3 h-3" style={{ color: '#E7B64B' }} />
          <span style={{ ...V.label, fontSize: 12, color: C.lime }}>Free taste: {freeSet.size} crops · Unlock the full handbook with Premium</span>
        </button>
      )}

      {/* Sow-now filter + Learning Hub link (climate lives on Today & Weather) */}
      <div className="mt-3 flex items-center flex-wrap" style={{ gap: 8 }}>
        <button onClick={() => setSowNowOnly((v) => !v)} aria-pressed={sowNowOnly} className="hg-press flex items-center rounded-full transition-colors" style={{ gap: 6, background: sowNowOnly ? C.forest : C.leafTint, padding: '8px 14px' }} data-testid="climate-sowable">
          <span className="rounded-full" style={{ width: 7, height: 7, background: sowNowOnly ? C.lime : C.leaf }} />
          <span style={{ ...V.label, fontSize: 12, color: sowNowOnly ? C.lime : C.leafDeep }}>{sowableNow} {t('crops.toSowNow')}{currentMonth ? ` · ${currentMonth}` : ''}</span>
        </button>
        <Link to="/learn" data-testid="handbook-learn-link" className="hg-press flex items-center rounded-full" style={{ gap: 6, background: C.cream, padding: '8px 14px', boxShadow: CARD_SH }}>
          <GraduationCap className="w-[15px] h-[15px]" style={{ color: C.gold }} />
          <span style={{ ...V.label, fontSize: 12, color: C.forest }}>{t('crops.learnLinkTitle')}</span>
        </Link>
      </div>

      {/* Planning next season entry */}
      <button onClick={() => setShortlistOpen(true)} data-testid="open-shortlist"
        className="hg-press w-full mt-3 flex items-center rounded-[16px] text-left"
        style={{ gap: 12, background: C.cream, padding: 12, boxShadow: CARD_SH }}>
        <span className="rounded-full flex items-center justify-center flex-shrink-0" style={{ width: 36, height: 36, background: C.goldTint }}>
          <Bookmark className="w-4 h-4" style={{ color: C.gold }} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block" style={V.bodySemi}>{t('crops.planningNextSeason')}</span>
          <span className="block" style={V.small}>{shortlist.length > 0 ? `${shortlist.length} crop${shortlist.length === 1 ? '' : 's'} shortlisted` : 'Bookmark crops to plan ahead'}</span>
        </span>
        <ChevronRight className="w-[18px] h-[18px] flex-shrink-0" style={{ color: C.muted }} />
      </button>

      {/* Search */}
      <div className="mt-3 flex items-center rounded-[16px]" style={{ gap: 8, background: C.white, padding: '0 16px', height: 48, boxShadow: CARD_SH }}>
        <Search className="w-[18px] h-[18px]" style={{ color: C.muted }} />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('crops.searchPlaceholder')} data-testid="search-crops-input"
          className="flex-1 h-full bg-transparent outline-none" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 15, color: C.text }} />
      </div>

      {/* Category chips — single horizontal scroll */}
      <div className="flex overflow-x-auto hg-scroll mt-3 mb-3 pb-1" style={{ gap: 8 }} data-testid="category-chips">
        {categories.map((item) => {
          const active = category === item;
          return (
            <button key={item} onClick={() => setCategory(item)} data-testid={`category-${item}`}
              className="hg-press rounded-full flex-shrink-0"
              style={{ padding: '8px 16px', background: active ? C.forest : C.cream, boxShadow: CARD_SH }}>
              <span style={{ ...V.label, color: active ? C.cream : C.forest }}>{item === 'all' ? t('crops.all') : item}</span>
            </button>
          );
        })}
      </div>

      {/* Active filters — Sow-Now stacks with category & search */}
      {(category !== 'all' || sowNowOnly || search) && (
        <div className="flex items-center flex-wrap mb-3" style={{ gap: 8 }} data-testid="active-filters">
          <span style={V.small} data-testid="results-count">{filtered.length} result{filtered.length === 1 ? '' : 's'}</span>
          {category !== 'all' && <ActivePill label={category} onClear={() => setCategory('all')} testid="active-category" />}
          {sowNowOnly && <ActivePill label="Sow now" onClear={() => setSowNowOnly(false)} testid="active-sownow" />}
          {search && <ActivePill label={`“${search}”`} onClear={() => setSearch('')} testid="active-search" />}
          <button onClick={() => { setCategory('all'); setSowNowOnly(false); setSearch(''); }} data-testid="clear-filters" className="hg-press ml-1" style={{ ...V.label, fontSize: 12, color: C.terracotta }}>Clear all</button>
        </div>
      )}

      {/* Crop grid — mobile card, responsive columns (same dimensions) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(178px, 1fr))', gap: 12 }} data-testid="crop-grid">
        {filtered.map((crop) => {
          const cal = calendar[crop.crop_id];
          const locked = !member && freeSet && !freeSet.has(crop.crop_id);
          const diff = DIFF[crop.difficulty] || DIFF.beginner;
          const sowText = cal
            ? (cal.currently_sowable ? (cal.year_round ? 'Sow year-round' : 'Sow now') : (cal.next_sow_month ? `From ${cal.next_sow_month}` : 'See guide'))
            : null;
          return (
            <Link key={crop.crop_id} to={locked ? '/paywall' : `/crops/${crop.crop_id}`} data-testid={`crop-card-${crop.crop_id}`}
              onClick={(e) => { if (locked) { e.preventDefault(); navigate('/paywall'); } else { openGuide(e, crop); } }}
              className="relative block overflow-hidden transition-transform duration-150 active:scale-[0.97]"
              style={{ height: 180, borderRadius: 20, background: C.forest, boxShadow: CARD_SH }}>
              <img src={cropImage(crop.name, crop.category, crop.image_url)} alt={crop.name} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover" data-testid={`crop-img-${crop.crop_id}`} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,45,31,0.08), rgba(15,45,31,0.45), rgba(15,45,31,0.9))' }} />
              {locked && (
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-testid={`crop-locked-${crop.crop_id}`}
                  style={{ backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', background: 'rgba(15,45,31,0.5)', zIndex: 3 }}>
                  <span className="flex items-center justify-center rounded-full" style={{ width: 36, height: 36, background: C.forestDeep, border: '1px solid rgba(231,182,75,0.6)', boxShadow: '0 0 18px -2px rgba(231,182,75,0.85)' }}>
                    <Lock className="w-4 h-4" style={{ color: '#E7B64B' }} />
                  </span>
                  <span className="mt-2 text-[11px] font-bold" style={{ color: C.lime }}>Unlock with Premium</span>
                </div>
              )}
              {/* card top */}
              <div className="absolute flex items-center justify-between" style={{ top: 12, left: 12, right: 12 }}>
                <span className="rounded-full" style={{ background: diff.bg, padding: '3px 8px' }}>
                  <span style={{ ...V.label, fontSize: 10, color: diff.fg, textTransform: 'capitalize' }}>{crop.difficulty}</span>
                </span>
                <button onClick={(e) => toggleSave(e, crop)} data-testid={`bookmark-${crop.crop_id}`}
                  className="flex items-center justify-center rounded-full" style={{ width: 32, height: 32, background: 'rgba(15,45,31,0.45)' }}>
                  <Bookmark className="w-[18px] h-[18px]" style={{ color: savedSet.has(crop.crop_id) ? C.gold : C.cream, fill: savedSet.has(crop.crop_id) ? C.gold : 'none' }} />
                </button>
              </div>
              {/* card bottom */}
              <div className="absolute" style={{ left: 12, right: 12, bottom: 12 }}>
                <p className="truncate" style={{ ...V.title, color: C.cream }}>{crop.name}</p>
                <p className="truncate" style={{ ...V.small, color: 'rgba(250,246,236,0.8)' }}>{crop.category}</p>
                {sowText && (
                  <span className="inline-flex items-center rounded-full mt-2" style={{ gap: 4, padding: '4px 8px', background: cal?.currently_sowable ? 'rgba(180,211,48,0.9)' : 'rgba(250,246,236,0.85)' }} data-testid={`sow-chip-${crop.crop_id}`}>
                    <Sun className="w-2.5 h-2.5" style={{ color: C.forest }} />
                    <span style={{ ...V.label, fontSize: 10, color: C.forest }}>{sowText}</span>
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
      {filtered.length === 0 && (
        <div className="text-center" style={{ padding: 32 }}><span style={V.small}>{sowNowOnly ? 'Nothing to sow right now in this filter.' : 'No crops match your search.'}</span></div>
      )}

      {zoom && createPortal((() => {
        const vw = window.innerWidth, vh = window.innerHeight;
        const tW = Math.min(vw - 48, 1000);
        const tLeft = (vw - tW) / 2;
        const tTop = Math.max(24, Math.round(vh * 0.08));
        const tH = Math.min(vh - tTop - 32, 460);
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 120 }} data-testid="guide-zoom">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.28 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(15,45,31,0.42)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' }} />
            <motion.img src={zoom.img} alt=""
              style={{ position: 'fixed', objectFit: 'cover', boxShadow: '0 40px 90px -24px rgba(15,45,31,0.65)', zIndex: 121 }}
              initial={{ top: zoom.rect.top, left: zoom.rect.left, width: zoom.rect.width, height: zoom.rect.height, borderRadius: 20 }}
              animate={{ top: tTop, left: tLeft, width: tW, height: tH, borderRadius: 26 }}
              transition={{ duration: 0.46, ease: [0.22, 0.61, 0.36, 1] }}
              onAnimationComplete={() => navigate(`/crops/${zoom.id}`)}
            />
          </div>
        );
      })(), document.body)}

      {shortlistOpen && (
        <div className="fixed inset-0 z-[70] bg-[#0F2D1F]/45 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={() => setShortlistOpen(false)} data-testid="shortlist-modal">
          <div className="bg-[#FAF6EC] rounded-3xl w-full max-w-md max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 style={V.h3}>{t('crops.planningNextSeason')}</h3>
              <button onClick={() => setShortlistOpen(false)} data-testid="shortlist-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center"><X className="w-5 h-5" style={{ color: C.muted }} /></button>
            </div>
            {shortlist.length === 0 ? (
              <div className="text-center py-10" style={{ color: C.muted }} data-testid="shortlist-empty">
                <Bookmark className="w-10 h-10 mx-auto mb-3" style={{ color: '#8EAA97' }} />
                Tap the bookmark on any crop to save it here for next season.
              </div>
            ) : (
              <div className="space-y-2">
                {shortlist.map((s) => (
                  <div key={s.crop_id} data-testid={`shortlist-item-${s.crop_id}`} className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: C.leafTint }}><Sprout className="w-4 h-4" style={{ color: C.leaf }} /></div>
                    <Link to={`/crops/${s.crop_id}`} onClick={() => setShortlistOpen(false)} className="flex-1 min-w-0">
                      <p className="font-semibold truncate" style={{ color: C.forest }}>{s.crop_name}</p>
                      {s.category && <p className="text-xs" style={{ color: '#7A6B57' }}>{s.category}</p>}
                    </Link>
                    <button onClick={(e) => toggleSave(e, { crop_id: s.crop_id, name: s.crop_name, category: s.category })} data-testid={`shortlist-remove-${s.crop_id}`} className="w-8 h-8 rounded-full bg-[#C66B4A]/12 hover:bg-[#C66B4A]/20 flex items-center justify-center"><X className="w-4 h-4" style={{ color: C.terracotta }} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

export default CropLibrary;
